import {useMemo} from 'react';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {isSectionPanelsAuto} from '../../state/views/compress';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../util/panelbankTypes';
import {keyExistsInQueryString} from '../../util/query-string';
import {useWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {SectionWithRef} from './types';

export const useIsSectionVisible = ({
  activePanelsCount,
  filteredPanelsCount,
  isHiddenPanelsSection,
  isSectionNameSearchMatch,
  sectionWithRef,
}: {
  /**
   * Total number of sections that could be rendered
   */
  activePanelsCount: number;
  /**
   * Total number of active panels that match the search regex
   */
  filteredPanelsCount: number;
  isHiddenPanelsSection: boolean;
  isSectionNameSearchMatch: boolean;
  sectionWithRef: SectionWithRef;
}) => {
  const {isSearching} = usePanelSearchDebouncedContext();
  const {workspaceLayout} = useWorkspaceSettingsContext();

  // Note: order of checks matter here!
  return useMemo(() => {
    // for dev debugging purposes, we should show the "Hidden panels" section
    // if the proper query string is set. otherwise, hide it from user
    if (isHiddenPanelsSection) {
      return keyExistsInQueryString('SHOW_HIDDEN_SECTION');
    }

    const hasActivePanels = activePanelsCount > 0;

    // TODO - add scrollToPanelBankSectionId logic

    // showEmptySections is a user workspace setting that applies during search
    if (isSearching && workspaceLayout.showEmptySections) {
      return true;
    }

    // show section if not searching and it has active panels
    if (!isSearching && hasActivePanels) {
      return true;
    }

    // show section if searching and section name or any panels match the search
    if (
      isSearching &&
      ((isSectionNameSearchMatch && hasActivePanels) || filteredPanelsCount > 0)
    ) {
      return true;
    }

    // Edge case: sections with no panels might be newly-created by the user.
    // We want to show these sections so panels can be dragged into them
    if (
      sectionWithRef.panels.length === 0 &&
      !isSectionPanelsAuto(sectionWithRef, workspaceLayout)
    ) {
      return true;
    }

    return false;
  }, [
    activePanelsCount,
    filteredPanelsCount,
    isHiddenPanelsSection,
    isSearching,
    isSectionNameSearchMatch,
    sectionWithRef,
    workspaceLayout,
  ]);
};

export const getIsHiddenPanelsSection = ({
  idx,
  numSections,
  sectionName,
}: {
  /**
   * Index of the section in the panel bank
   */
  idx: number;
  /**
   * Total number of sections that could be rendered
   */
  numSections: number;
  sectionName: string;
}) => {
  const isLastSection = idx === numSections - 1;
  const isHiddenPanelsSection =
    isLastSection && sectionName === PANEL_BANK_HIDDEN_SECTION_NAME;

  return isHiddenPanelsSection;
};
